// Client-side PGN builder (plan Item A1). Turns a `Review` (the shared move model)
// plus a `PgnMeta` into a spec-compliant PGN string: the Seven Tag Roster, a small
// set of extras, then wrapped movetext and the result token. This is the single
// export path for the Analysis board (A3) and the mirror of the server's bulk
// exporter (A4) — the tag order + result mapping here MUST match `game_to_pgn` in
// `crates/server/src/game/pgn.rs`, pinned by a golden-string test on each side.

import { INITIAL_FEN } from "chessops/fen";
import type { Review } from "./review.ts";
import type { BotGameRecord } from "./botHistory.ts";
import type { Color, FullGame } from "./types.ts";
import { slugName } from "../ui/clipboard.ts";
import { t } from "../i18n/index.ts";

/** Everything the header block can carry. Only `white`/`black`/`result` are
 *  required; the rest are emitted when present, in the fixed order below. */
export interface PgnMeta {
  clockMode?: string;
  lifecycle?: string;
  importedPlies?: number;
  event?: string;
  site?: string;
  date?: string; // "YYYY.MM.DD" (PGN uses "????.??.??" for unknown)
  round?: string;
  white: string;
  black: string;
  result: string; // "1-0" | "0-1" | "1/2-1/2" | "*"
  whiteElo?: string | number;
  blackElo?: string | number;
  timeControl?: string; // PGN "base+inc" seconds form, e.g. "300+3", or "-"
  eco?: string;
  opening?: string;
  termination?: string;
}

const SITE = "chess-me.com";

/** Build a full PGN document for one game. Ends with a trailing newline so a
 *  downloaded `.pgn` is well-formed and multi-game concatenation stays clean. */
export function buildPgn(review: Review, meta: PgnMeta): string {
  const tags: [string, string][] = [
    ["Event", meta.event || "ChessME"],
    ["Site", meta.site || SITE],
    ["Date", meta.date || "????.??.??"],
    ["Round", meta.round || "?"],
    ["White", meta.white || "?"],
    ["Black", meta.black || "?"],
    ["Result", meta.result || "*"],
  ];
  if (meta.whiteElo) tags.push(["WhiteElo", String(meta.whiteElo)]);
  if (meta.blackElo) tags.push(["BlackElo", String(meta.blackElo)]);
  if (meta.timeControl) tags.push(["TimeControl", meta.timeControl]);
  if (meta.eco) tags.push(["ECO", meta.eco]);
  if (meta.opening) tags.push(["Opening", meta.opening]);
  if (meta.termination) tags.push(["Termination", meta.termination]);
  if (meta.clockMode === "untimed") tags.push(["ChessMEClockMode", "untimed"]);
  if (meta.lifecycle === "paused") tags.push(["ChessMELifecycle", "paused"]);
  if ((meta.importedPlies ?? 0) > 0) tags.push(["ChessMEImportedPlies", String(meta.importedPlies)]);
  // A non-standard start position needs SetUp + FEN so the game replays.
  if (review.initialFen !== INITIAL_FEN) {
    tags.push(["SetUp", "1"]);
    tags.push(["FEN", review.initialFen]);
  }

  const header = tags.map(([k, v]) => `[${k} "${escapeTag(v)}"]`).join("\n");
  const movetext = buildMovetext(review, meta.result || "*");
  return `${header}\n\n${movetext}\n`;
}

/** Movetext with move numbers, wrapped at ~80 columns, result token appended.
 *  Honours a non-standard start (side to move + fullmove number from the FEN).
 *  A node's imported `comment` is re-emitted as `{…}` after its move (plan B1);
 *  a comment before a black move forces the `N...` continuation number. Server
 *  games carry no comments, so their output is byte-identical to before. */
function buildMovetext(review: Review, result: string): string {
  const fields = review.initialFen.split(" ");
  const startBlack = fields[1] === "b";
  let fullmove = Number.parseInt(fields[5] ?? "1", 10) || 1;

  const tokens: string[] = [];
  let blackNeedsNumber = startBlack; // "N..." before a black move after a break
  for (let i = 1; i < review.nodes.length; i++) {
    const node = review.nodes[i];
    const san = node.san ?? "";
    if (node.moveColor === "white") {
      tokens.push(`${fullmove}.`, san);
    } else {
      if (blackNeedsNumber) tokens.push(`${fullmove}...`);
      tokens.push(san);
      fullmove++;
    }
    blackNeedsNumber = false;
    if (node.comment) {
      // Braces can't nest / self-terminate in PGN — strip them from the text.
      // Word-by-word tokens so the 80-column wrap applies inside the comment.
      const words = node.comment.replace(/[{}]/g, "").split(/\s+/).filter(Boolean);
      if (words.length > 0) {
        words[0] = `{${words[0]}`;
        words[words.length - 1] += "}";
        tokens.push(...words);
        // A comment interrupts the pair — the following black move needs "N...".
        if (node.moveColor === "white") blackNeedsNumber = true;
      }
    }
  }
  tokens.push(result);
  return wrap(tokens, 80);
}

/** Join space-separated tokens into lines no longer than `max` columns. */
function wrap(tokens: string[], max: number): string {
  const lines: string[] = [];
  let line = "";
  for (const tok of tokens) {
    if (line === "") line = tok;
    else if (line.length + 1 + tok.length <= max) line += ` ${tok}`;
    else {
      lines.push(line);
      line = tok;
    }
  }
  if (line) lines.push(line);
  return lines.join("\n");
}

/** Escape a tag value for the quoted PGN form (backslash first, then quote). */
function escapeTag(v: string): string {
  return v.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

// ---------------------------------------------------------------------------
// Mappers so callers don't duplicate result/termination/time-control logic.
// ---------------------------------------------------------------------------

/** Map an app status + winner to a PGN result token. */
export function resultToken(status: string, winner: Color | null): string {
  if (winner === "white") return "1-0";
  if (winner === "black") return "0-1";
  if (status === "started" || status === "aborted" || status === "returned") return "*";
  return "1/2-1/2";
}

// Human-readable Termination, matching the Analysis board's REASON vocabulary
// (kept in sync with `crates/server/src/game/pgn.rs`, pinned by golden tests).
// Covers two vocabularies: bot games key off their own `status` (timeout,
// threefold, …); server games key off the DB `termination` column (flag,
// repetition, claim, agreement, abandonment).
export const TERMINATION: Record<string, string> = {
  mate: "Checkmate",
  resign: "Resignation",
  timeout: "Time out",
  flag: "Time out",
  stalemate: "Stalemate",
  insufficient: "Insufficient material",
  threefold: "Threefold repetition",
  repetition: "Repetition",
  claim: "Draw claimed",
  fifty: "Fifty-move rule",
  draw: "Draw",
  agreement: "Draw agreed",
  aborted: "Abandonment",
  abandonment: "Abandonment",
  // A live segment handed back to Messages: the game goes on there.
  returned: "Unterminated",
  lost: "Unterminated",
};

/** Termination tag text for a status/reason, or undefined for a live game.
 *  Prefers the specific `reason` (server `termination` column) over `status`.
 *  Stays ENGLISH: this feeds the PGN `[Termination]` tag (a data format that must
 *  mirror the server exporter). Localize the *display* via `terminationLabel` /
 *  the localized `pgnResultLine` instead. */
export function terminationText(status: string, reason?: string | null): string | undefined {
  if (status === "started") return undefined;
  const key = reason || status;
  return TERMINATION[key] ?? key;
}

// Reason/status key → localized display key. Mirrors TERMINATION's keys but points
// at the i18n catalog, so the on-screen text can be translated while the PGN tag
// (via terminationText) stays English.
const TERM_KEY: Record<string, string> = {
  mate: "term.checkmate",
  resign: "term.resignation",
  timeout: "term.timeout",
  flag: "term.timeout",
  stalemate: "term.stalemate",
  insufficient: "term.insufficient",
  threefold: "term.repetition",
  repetition: "term.repetitionShort",
  claim: "term.drawClaimed",
  fifty: "term.fiftyMove",
  draw: "term.draw",
  agreement: "term.drawAgreed",
  aborted: "term.abandonment",
  abandonment: "term.abandonment",
  returned: "term.returned",
  lost: "term.returned",
};

// Inverse of TERMINATION (English display text → localized key), so a stored PGN's
// English `Termination` tag can be shown localized in the result line.
const TERM_TEXT_KEY: Record<string, string> = {
  Checkmate: "term.checkmate",
  Resignation: "term.resignation",
  "Time out": "term.timeout",
  Stalemate: "term.stalemate",
  "Insufficient material": "term.insufficient",
  "Threefold repetition": "term.repetition",
  Repetition: "term.repetitionShort",
  "Draw claimed": "term.drawClaimed",
  "Fifty-move rule": "term.fiftyMove",
  Draw: "term.draw",
  "Draw agreed": "term.drawAgreed",
  Abandonment: "term.abandonment",
  Unterminated: "term.returned",
};

/** Localized termination label for a reason/status key (on-screen display). */
export function terminationLabel(key: string): string {
  const k = TERM_KEY[key];
  return k ? t(k) : key;
}

/** A human result line from a PGN meta (result token + termination), localized.
 *  The English `meta.termination` (PGN tag) is mapped to its localized display. */
export function pgnResultLine(meta: PgnMeta): string {
  const base =
    meta.result === "1-0"
      ? t("pgn.whiteWins")
      : meta.result === "0-1"
        ? t("pgn.blackWins")
        : meta.result === "1/2-1/2"
          ? t("pgn.draw")
          : t("pgn.game");
  if (!meta.termination) return base;
  const key = TERM_TEXT_KEY[meta.termination];
  const term = key ? t(key) : meta.termination;
  return `${base} · ${term}`;
}

/** Centiseconds base/inc → PGN "base+inc" seconds (e.g. 30000,300 → "300+3"). */
export function tcFromCs(baseCs: number, incCs: number): string | undefined {
  if (!baseCs) return undefined;
  return `${Math.round(baseCs / 100)}+${Math.round(incCs / 100)}`;
}

/** A bot-game TC label ("5+3" | "unlimited") → PGN seconds form ("300+3" | "-"). */
export function tcFromLabel(tc: string): string {
  if (tc === "unlimited") return "-";
  const m = /^(\d+)\+(\d+)$/.exec(tc);
  if (!m) return "-";
  return `${Number(m[1]) * 60}+${Number(m[2])}`;
}

/** Unix milliseconds → PGN date "YYYY.MM.DD". */
export function pgnDate(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`;
}

/** A filesystem-safe `.pgn` filename for a game: `white-vs-black-YYYY-MM-DD.pgn`,
 *  using the game's real date when known and today's otherwise. Shared by every
 *  download call site (Analysis, Study, History) so the logic can't diverge. */
export function pgnFilename(meta: PgnMeta): string {
  return `${slugName(meta.white)}-vs-${slugName(meta.black)}-${dateSlug(meta.date)}.pgn`;
}

/** A PGN date ("YYYY.MM.DD", possibly *partial* like "1852.??.??") → a
 *  filename-safe "YYYY-MM-DD" slug. Sanitizes the WHOLE string (not just "." →
 *  "-"), so a partial date can't leak a literal "?" (illegal on Windows) into the
 *  filename; an unknown/garbage date falls back to today. */
function dateSlug(date: string | undefined): string {
  if (date) {
    const safe = date
      .replace(/\./g, "-")
      .replace(/[^0-9-]/g, "") // drop "?" and any other unsafe char
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (safe) return safe; // e.g. "1852.??.??" → "1852", "2025.07.08" → "2025-07-08"
  }
  return new Date().toISOString().slice(0, 10);
}

/** PgnMeta for a server game snapshot (Elo isn't in the snapshot). The played
 *  date comes from the finish (else start) instant — unix seconds → ms. */
export function metaFromServerGame(full: FullGame): PgnMeta {
  const stamp = full.finished_at ?? full.started_at;
  return {
    event: "ChessME",
    white: full.white.name,
    black: full.black.name,
    date: stamp ? pgnDate(stamp * 1000) : undefined,
    result: resultToken(full.status, full.winner),
    timeControl: full.clock_mode === "untimed" ? undefined : tcFromCs(full.base_cs, full.inc_cs),
    clockMode: full.clock_mode, lifecycle: full.lifecycle, importedPlies: full.imported_ply_count,
    termination: terminationText(full.status, full.termination),
  };
}

/** PgnMeta for a local computer game record. */
export function metaFromBotGame(rec: BotGameRecord): PgnMeta {
  return {
    event: "Computer game",
    white: rec.humanColor === "white" ? "You" : "Computer",
    black: rec.humanColor === "black" ? "You" : "Computer",
    date: pgnDate(rec.createdAt),
    result: resultToken(rec.status, rec.winner),
    timeControl: tcFromLabel(rec.tc),
    termination: terminationText(rec.status),
  };
}
